import React from "react";
import {createUseStyles} from "react-jss";

import {Alternative} from "./Alternative";
import {Typography} from "../common/Typography";
import {useCurrentQuestion} from "./useCurrentQuestion";
import {useNavigation} from "../navigation/useNavigation";
import {useCurrentAnswer} from "../answer/useCurrentAnswer";
import {QuestionMedia} from "./QuestionMedia";
import {useQuizState} from "../quiz/quizContext";
import {SafeRichTextRenderer} from "../common/richText/SafeRichTextRenderer";

const useStyles = createUseStyles((theme = {}) => ({
    question: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: "1200px",
        width: "90%",
        boxSizing: "border-box",
        flexGrow: 1,
        gap: "20px"
    },
    questionText: {
        textAlign: "center",
        minHeight: "90px",
        "@media (max-width: 600px)": {
            fontSize: theme?.fontSize?.xl,
            lineHeight: theme?.lineHeight?.leadingRelaxed,
        },
    },
    alternatives: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "20px",
        width: "100%",
        maxWidth: "1200px",
        justifyItems: "center",
        "@media (max-width: 900px)": {
            gridTemplateColumns: "1fr",
            gap: "12px",
        },
    },
    explanationCallout: {
        width: "100%",
        maxWidth: "1200px",
        boxSizing: "border-box",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        borderLeft: "4px solid #4ade80",
        borderRadius: "8px",
        padding: "16px 20px",
        marginTop: "16px",
        textAlign: "left",
        color: "#ffffff",
    },
    explanationTitle: {
        fontWeight: "bold",
        fontSize: "1.1rem",
        marginBottom: "8px",
        color: "#4ade80",
    },
}))

export const Question = () => {
    const classes = useStyles();
    const alternatives = ["a", "b", "c", "d", "e", "f"];
    const question = useCurrentQuestion();
    const quizState = useQuizState();
    const immediateFeedback = quizState?.immediateFeedback ?? quizState?.quiz?.immediateFeedback ?? false;
    const {
        answerUniqueId,
        selectAnswer
    } = useCurrentAnswer();
    const {
        questionIndex
    } = useNavigation();

    const selectedUniqueId = question?.selectedAnswerUniqueId || answerUniqueId;
    const hasImmediateExplanation = Boolean(
        immediateFeedback &&
        question?.answerRevealed &&
        question?.details &&
        question.details.trim().length > 0
    );

    return (
        <div className={classes.question}>
            <Typography
                tag="h1"
                size="xl4"
                bold
                className={classes.questionText}
            >
                {questionIndex + 1}. {question?.content}
            </Typography>
            <QuestionMedia
               media={question?.breadcrumbMedia || question?.media}
            />
            <div className={classes.alternatives}>
                {(question?.answers || [])
                    .filter(a => a && a.active !== false)
                    .map((answer, idx) => (
                        <Alternative
                            key={answer.uniqueId}
                            answer={answer}
                            option={alternatives[idx]}
                            onClick={() => selectAnswer(answer.uniqueId)}
                            selected={selectedUniqueId === answer.uniqueId}
                            hasMedia={Boolean(question?.breadcrumbMedia || question?.media)}
                            answerRevealed={Boolean(question?.answerRevealed)}
                        />
                ))}
            </div>
            {hasImmediateExplanation && (
                <div
                    role="region"
                    aria-label="Forklaring"
                    aria-live="polite"
                    data-testid="immediate-question-explanation"
                    className={classes.explanationCallout}
                >
                    <div className={classes.explanationTitle}>Forklaring</div>
                    <SafeRichTextRenderer text={question.details} />
                </div>
            )}
        </div>
    );
};
